/**
 * Painel flutuante: incluir aprovações de compra pendentes na projeção de saldo.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ClipboardCheck, X, Loader2, AlertCircle, ChevronRight,
  CheckSquare, Square, Calendar, ExternalLink
} from 'lucide-react'
import { financeService } from '../../../../services/purchasesApi'
import {
  buildRequisitionPaymentSchedule,
  getRequisitionTotalValue
} from '../../../../utils/purchaseProjectionUtils'
import { formatDateOnly } from '../../../../utils/dateUtils'
import './PendingPurchasesProjectionPanel.css'

function formatCurrency(value) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value || 0)
}

const PendingPurchasesProjectionPanel = ({
  selectedIds,
  onSelectionChange,
  onRequisitionsLoaded
}) => {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [requisitions, setRequisitions] = useState([])

  const loadPending = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const listRes = await financeService.getList()
      if (!listRes?.success) {
        throw new Error(listRes?.message || 'Erro ao carregar aprovações pendentes')
      }

      const baseList = listRes.data || []
      const enriched = await Promise.all(
        baseList.map(async (req) => {
          if (req.payment_method !== 'installments') {
            return req
          }
          try {
            const detail = await financeService.getById(req.id)
            if (detail?.success && detail.data?.payments?.length) {
              return { ...req, ...detail.data, payments: detail.data.payments }
            }
          } catch {
            /* usa campos da listagem */
          }
          return req
        })
      )

      setRequisitions(enriched)
      onRequisitionsLoaded?.(enriched)
    } catch (err) {
      console.error(err)
      setError(err.response?.data?.message || err.message || 'Erro ao carregar compras')
      setRequisitions([])
      onRequisitionsLoaded?.([])
    } finally {
      setLoading(false)
    }
  }, [onRequisitionsLoaded])

  useEffect(() => {
    if (open && requisitions.length === 0 && !loading && !error) {
      loadPending()
    }
  }, [open, requisitions.length, loading, error, loadPending])

  const selectableIds = useMemo(() => {
    return requisitions
      .filter((req) => buildRequisitionPaymentSchedule(req).ready)
      .map((req) => req.id)
  }, [requisitions])

  const allSelectableSelected =
    selectableIds.length > 0 && selectableIds.every((id) => selectedIds.includes(id))

  const toggleOne = (id) => {
    const req = requisitions.find((r) => r.id === id)
    const { ready } = buildRequisitionPaymentSchedule(req)
    if (!ready) return

    if (selectedIds.includes(id)) {
      onSelectionChange(selectedIds.filter((x) => x !== id))
    } else {
      onSelectionChange([...selectedIds, id])
    }
  }

  const toggleAll = () => {
    if (allSelectableSelected) {
      onSelectionChange([])
    } else {
      onSelectionChange([...selectableIds])
    }
  }

  const selectedCount = selectedIds.length
  const selectedTotal = useMemo(() => {
    return requisitions
      .filter((r) => selectedIds.includes(r.id))
      .reduce((sum, r) => sum + getRequisitionTotalValue(r), 0)
  }, [requisitions, selectedIds])

  return (
    <div className="pending-purchases-root">
      <button
        type="button"
        className={`pending-purchases-fab ${selectedCount > 0 ? 'has-selection' : ''}`}
        onClick={() => {
          setOpen((v) => !v)
          if (!open) loadPending()
        }}
        title="Incluir compras pendentes na projeção"
        aria-expanded={open}
        aria-label="Compras pendentes na projeção"
      >
        <ClipboardCheck size={18} />
        {selectedCount > 0 && (
          <span className="pending-purchases-fab-badge">{selectedCount}</span>
        )}
      </button>

      {open && (
        <div className="pending-purchases-panel" role="dialog" aria-label="Compras na projeção">
          <div className="pending-purchases-panel-header">
            <div>
              <h4 className="pending-purchases-panel-title">
                Compras <span className="pending-purchases-panel-accent">Pendentes</span>
              </h4>
              <p className="pending-purchases-panel-subtitle">
                Selecione pedidos em aprovação financeira para simular no gráfico
              </p>
            </div>
            <button
              type="button"
              className="pending-purchases-panel-close"
              onClick={() => setOpen(false)}
              aria-label="Fechar"
            >
              <X size={18} />
            </button>
          </div>

          <div className="pending-purchases-panel-toolbar">
            <button
              type="button"
              className="pending-purchases-select-all"
              onClick={toggleAll}
              disabled={selectableIds.length === 0}
            >
              {allSelectableSelected ? <CheckSquare size={16} /> : <Square size={16} />}
              <span>{allSelectableSelected ? 'Desmarcar todas' : 'Selecionar todas'}</span>
            </button>
            <button
              type="button"
              className="pending-purchases-refresh"
              onClick={loadPending}
              disabled={loading}
            >
              Atualizar
            </button>
          </div>

          <div className="pending-purchases-panel-body">
            {loading && (
              <div className="pending-purchases-state">
                <Loader2 size={22} className="pending-purchases-spin" />
                <span>Carregando aprovações...</span>
              </div>
            )}

            {error && !loading && (
              <div className="pending-purchases-state pending-purchases-state-error">
                <AlertCircle size={20} />
                <span>{error}</span>
                <button type="button" onClick={loadPending}>Tentar novamente</button>
              </div>
            )}

            {!loading && !error && requisitions.length === 0 && (
              <div className="pending-purchases-state">
                <span>Nenhuma compra aguardando aprovação financeira</span>
              </div>
            )}

            {!loading && !error && requisitions.length > 0 && (
              <ul className="pending-purchases-list">
                {requisitions.map((req) => {
                  const meta = buildRequisitionPaymentSchedule(req)
                  const selected = selectedIds.includes(req.id)
                  const total = getRequisitionTotalValue(req)

                  return (
                    <li key={req.id} className={`pending-purchases-item ${selected ? 'selected' : ''}`}>
                      <button
                        type="button"
                        className="pending-purchases-item-check"
                        onClick={() => toggleOne(req.id)}
                        disabled={!meta.ready}
                        aria-pressed={selected}
                      >
                        {selected ? <CheckSquare size={18} /> : <Square size={18} />}
                      </button>

                      <div className="pending-purchases-item-content">
                        <div className="pending-purchases-item-top">
                          <span className="pending-purchases-item-id">#{req.id}</span>
                          <span className="pending-purchases-item-value">{formatCurrency(total)}</span>
                        </div>
                        <p className="pending-purchases-item-title">{req.title || 'Sem título'}</p>

                        {meta.ready ? (
                          <div className="pending-purchases-schedule">
                            <Calendar size={12} />
                            {meta.schedule.slice(0, 3).map((inst) => (
                              <span key={`${req.id}-${inst.installmentNumber}`} className="pending-purchases-due">
                                {formatDateOnly(inst.dueDate)} · {formatCurrency(inst.amount)}
                              </span>
                            ))}
                            {meta.schedule.length > 3 && (
                              <span className="pending-purchases-due-more">
                                +{meta.schedule.length - 3} parcelas
                              </span>
                            )}
                          </div>
                        ) : (
                          <p className="pending-purchases-item-warning">{meta.reason}</p>
                        )}
                      </div>

                      <button
                        type="button"
                        className="pending-purchases-item-link"
                        onClick={() => navigate(`/erp/purchases/finance/approve/${req.id}`)}
                        title="Abrir aprovação"
                      >
                        <ExternalLink size={14} />
                      </button>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>

          {selectedCount > 0 && (
            <div className="pending-purchases-panel-footer">
              <div>
                <span className="pending-purchases-footer-label">Impacto simulado</span>
                <strong>{formatCurrency(selectedTotal)}</strong>
              </div>
              <button
                type="button"
                className="pending-purchases-footer-link"
                onClick={() => navigate('/erp/purchases/finance')}
              >
                Ver fila <ChevronRight size={14} />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default PendingPurchasesProjectionPanel
